import type { LucideIcon } from 'lucide-react'

import { AppLink } from '@/shared/lib/router'
import { Button } from '@/shared/ui/Button'
import { Card, CardBody, CardHeader, CardTitle } from '@/shared/ui/Card'

interface EntityItem {
  id: number
  nome: string
  extra?: string | null
}

interface EmptyCta {
  label: string
  href: string
}

export function EntityRows({
  items,
  icon: Icon
}: {
  items: EntityItem[]
  icon: LucideIcon
}) {
  return (
    <ul className="flex flex-col gap-1.5">
      {items.map(item => (
        <li
          key={item.id}
          className="flex items-center gap-2 rounded-xl border border-border bg-linear-to-t from-[#eaeeff]/50 to-white px-3 py-2 text-sm shadow-[0_2px_0_rgba(106,129,219,0.15)]"
        >
          <Icon className="size-3.5 shrink-0 text-primary" aria-hidden />
          <span className="min-w-0 flex-1 truncate font-semibold text-primary-dark">
            {item.nome}
          </span>
          {item.extra ? (
            <span className="shrink-0 text-xs font-bold text-muted-foreground">{item.extra}</span>
          ) : null}
        </li>
      ))}
    </ul>
  )
}

export function RichEmpty({
  icon: Icon,
  message,
  cta
}: {
  icon: LucideIcon
  message: string
  cta?: EmptyCta
}) {
  return (
    <div className="flex flex-col items-center gap-3 py-8 text-center">
      <span className="flex size-12 items-center justify-center rounded-2xl bg-muted text-muted-foreground">
        <Icon className="size-6" strokeWidth={1.5} aria-hidden />
      </span>
      <p className="max-w-xs text-sm font-semibold text-muted-foreground">{message}</p>
      {cta ? (
        <Button asChild variant="outline" size="sm">
          <AppLink href={cta.href}>{cta.label}</AppLink>
        </Button>
      ) : null}
    </div>
  )
}

interface EntityListCardProps {
  title: string
  headerIcon: LucideIcon
  rowIcon: LucideIcon
  items: EntityItem[]
  emptyMessage: string
  emptyCta?: EmptyCta
}

export function EntityListCard({
  title,
  headerIcon: HeaderIcon,
  rowIcon,
  items,
  emptyMessage,
  emptyCta
}: EntityListCardProps) {
  return (
    <Card variant="default">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <span className="text-primary">
            <HeaderIcon className="size-5" aria-hidden />
          </span>
          {title}
        </CardTitle>
      </CardHeader>
      <CardBody>
        {items.length ? (
          <EntityRows items={items} icon={rowIcon} />
        ) : (
          <RichEmpty icon={HeaderIcon} message={emptyMessage} cta={emptyCta} />
        )}
      </CardBody>
    </Card>
  )
}
